import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { FounderIconButton } from "../../../components/founder/FounderIconButton";
import { FounderOperationalShell } from "../../../components/founder/FounderOperationalShell";
import {
  FounderEmptyState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  createFounderLegalDocument,
  fetchFounderAuditLogs,
  fetchFounderLegalDocuments,
  fetchFounderMaintenanceMode,
  fetchFounderOperationalHealth,
  fetchFounderOperationalTemplates,
  fetchFounderPlatformSettings,
  fetchFounderScanners,
  fetchFounderSecurityAlerts,
  fetchFounderWorkspaceCounts,
  resolveFounderSecurityAlert,
  updateFounderMaintenanceMode,
  updateFounderPublicationMode,
} from "../../../founder/founderWorkspaceApi";
import { useFounderAuth } from "../../../context/FounderAuthContext";
import type { FounderSecuritySummary } from "../../../context/FounderAuthContext";
import {
  formatPublicationModeLabel,
  type FounderWorkspaceCounts,
} from "../../../services/founderHomeDashboard";
import {
  formatFounderAuditActivity,
  formatFounderSecurityActivity,
} from "../../../services/founderActivityLabels";

const TABS = [
  { id: "health", label: "System Health" },
  { id: "security", label: "Security" },
  { id: "publishing", label: "Publishing Rules" },
  { id: "legal", label: "Legal Documents" },
  { id: "audit", label: "Audit Log" },
] as const;

export function FounderPlatformWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "health";
  const { fetchSecuritySummary } = useFounderAuth();

  const [counts, setCounts] = useState<FounderWorkspaceCounts | null>(null);
  const [scanners, setScanners] = useState<Awaited<ReturnType<typeof fetchFounderScanners>>>([]);
  const [alerts, setAlerts] = useState<Awaited<ReturnType<typeof fetchFounderSecurityAlerts>>>([]);
  const [logs, setLogs] = useState<Awaited<ReturnType<typeof fetchFounderAuditLogs>>>([]);
  const [publicationMode, setPublicationMode] = useState("review_required");
  const [editingPublishing, setEditingPublishing] = useState(false);
  const [draftMode, setDraftMode] = useState<"review_required" | "instant">("review_required");
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [legalDocs, setLegalDocs] = useState<Awaited<ReturnType<typeof fetchFounderLegalDocuments>>>([]);
  const [maintenance, setMaintenance] = useState({ enabled: false, message: "" });
  const [confirmMaintenance, setConfirmMaintenance] = useState(false);
  const [templates, setTemplates] = useState<Record<string, { label: string; subject: string; body: string; deliveryConnected: boolean }>>({});
  const [healthLabels, setHealthLabels] = useState<Record<string, string>>({});
  const [resolveAlertId, setResolveAlertId] = useState<string | null>(null);
  const [resolveNote, setResolveNote] = useState("");
  const [loginSecurity, setLoginSecurity] = useState<FounderSecuritySummary | null>(null);
  const [legalDraft, setLegalDraft] = useState({
    documentType: "privacy_policy" as "publisher_agreement" | "privacy_policy" | "disclaimer",
    version: "",
    title: "",
    content: "",
    effectiveDate: new Date().toISOString().slice(0, 10),
  });
  const [creatingLegal, setCreatingLegal] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchFounderWorkspaceCounts(),
      fetchFounderScanners(),
      fetchFounderSecurityAlerts(20),
      fetchFounderAuditLogs(50),
      fetchFounderPlatformSettings(),
      fetchFounderLegalDocuments(),
      fetchFounderMaintenanceMode(),
      fetchFounderOperationalTemplates(),
      fetchFounderOperationalHealth(),
      fetchSecuritySummary(),
    ])
      .then(([workspaceCounts, scannerRows, alertRows, logRows, settings, docs, maintenanceMode, templateRows, health, securitySummary]) => {
        setCounts(workspaceCounts);
        setScanners(scannerRows);
        setAlerts(alertRows);
        setLogs(logRows);
        setPublicationMode(settings.publicationMode);
        setDraftMode(settings.publicationMode === "instant" ? "instant" : "review_required");
        setLegalDocs(docs);
        setMaintenance(maintenanceMode);
        setTemplates(templateRows);
        setHealthLabels(health.labels);
        setLoginSecurity(securitySummary);
      })
      .finally(() => setLoading(false));
  }, [fetchSecuritySummary]);

  function setTab(next: string) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  }

  async function publishPublishingRules(confirmPassword?: string) {
    if (!confirmPassword) {
      setMessage("Enter your current password to confirm this change.");
      return;
    }
    setSubmitting(true);
    setMessage("");
    try {
      await updateFounderPublicationMode(draftMode, confirmPassword);
      setPublicationMode(draftMode);
      setEditingPublishing(false);
      setConfirmPublish(false);
      setMessage("Publishing rules updated.");
    } catch {
      setMessage("Could not update publishing rules.");
    } finally {
      setSubmitting(false);
    }
  }

  const metrics = counts
    ? [
        {
          id: "db",
          label: "Database",
          value: counts.databaseStatus === "connected" ? "Connected" : "Check",
          tone: counts.databaseStatus === "connected" ? ("mint" as const) : ("coral" as const),
        },
        {
          id: "pay",
          label: "Payments",
          value: counts.paymentGatewayStatus === "configured" ? "Configured" : "Check",
          tone: counts.paymentGatewayStatus === "configured" ? ("teal" as const) : ("coral" as const),
        },
        {
          id: "sec",
          label: "Open alerts",
          value: alerts.length,
          tone: alerts.length > 0 ? ("coral" as const) : ("mint" as const),
        },
        {
          id: "pub",
          label: "Publishing",
          value: publicationMode === "instant" ? "Instant" : "Review",
          tone: "steel" as const,
        },
      ]
    : [];

  return (
    <FounderOperationalShell
      activeTab={tab}
      metrics={metrics}
      onTabChange={setTab}
      tabs={[...TABS]}
      title="Platform Control"
    >
      {loading ? <FounderLoadingState label="Loading platform data..." /> : null}

      {!loading && tab === "health" && counts ? (
        <div className="oac-founder-ops-grid">
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Core services</h2>
            <ul className="oac-founder-ops-list">
              <li className="oac-founder-ops-list__item">
                <span className="oac-founder-ops-list__title">Platform database</span>
                <span className={`oac-founder-badge oac-founder-badge--${counts.databaseStatus === "connected" ? "healthy" : "attention"}`}>
                  {healthLabels.database ?? counts.databaseStatus}
                </span>
              </li>
              <li className="oac-founder-ops-list__item">
                <span className="oac-founder-ops-list__title">Payment gateway</span>
                <span className={`oac-founder-badge oac-founder-badge--${counts.paymentGatewayStatus === "not_configured" ? "attention" : "healthy"}`}>
                  {healthLabels.payments ?? counts.paymentGatewayStatus}
                </span>
              </li>
            </ul>
          </section>
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Maintenance mode</h2>
            <label className="oac-founder-ops-radio">
              <input
                checked={maintenance.enabled}
                onChange={(e) => setMaintenance({ ...maintenance, enabled: e.target.checked })}
                type="checkbox"
              />
              Enable maintenance mode
            </label>
            <div className="oac-founder-field">
              <label htmlFor="maintenance-message">Customer message</label>
              <textarea
                id="maintenance-message"
                onChange={(e) => setMaintenance({ ...maintenance, message: e.target.value })}
                rows={3}
                value={maintenance.message}
              />
            </div>
            <FounderIconButton
              kind="publish"
              label="Save maintenance mode"
              onClick={() => setConfirmMaintenance(true)}
            />
            {confirmMaintenance ? (
              <FounderConfirmPublish
                message={
                  maintenance.enabled
                    ? "Turn on maintenance mode? Customers will see your message."
                    : "Save maintenance mode settings?"
                }
                onCancel={() => setConfirmMaintenance(false)}
                onConfirm={async (confirmPassword) => {
                  if (!confirmPassword) {
                    setMessage("Enter your current password to confirm this change.");
                    return;
                  }
                  try {
                    const next = await updateFounderMaintenanceMode({
                      ...maintenance,
                      confirmPassword,
                    });
                    setMaintenance(next);
                    setConfirmMaintenance(false);
                    setMessage("Maintenance mode updated.");
                  } catch {
                    setMessage("Could not update maintenance mode.");
                  }
                }}
                requirePassword
              />
            ) : null}
          </section>
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Operational communications</h2>
            <p className="oac-founder-ops-footnote">
              Template content is editable. Automated delivery is shown only where connected.
            </p>
            <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
              {Object.entries(templates).slice(0, 4).map(([key, template]) => (
                <li className="oac-founder-ops-list__item" key={key}>
                  <div>
                    <span className="oac-founder-ops-list__title">{template.label}</span>
                    <span className="oac-founder-ops-list__meta">
                      {template.deliveryConnected ? "Delivery connected" : "Content only"}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Security scanners</h2>
            {scanners.length === 0 ? (
              <FounderEmptyState message="No scanner engines configured." />
            ) : (
              <ul className="oac-founder-ops-list">
                {scanners.map((scanner) => (
                  <li className="oac-founder-ops-list__item" key={scanner.engine}>
                    <span className="oac-founder-ops-list__title">{scanner.engine}</span>
                    <span className={`oac-founder-badge oac-founder-badge--${scanner.available ? "healthy" : "problem"}`}>
                      {scanner.available ? "Available" : "Unavailable"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}

      {!loading && tab === "security" ? (
        <>
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Founder account</h2>
            {loginSecurity ? (
              <ul className="oac-founder-ops-list">
                <li className="oac-founder-ops-list__item">
                  <div>
                    <span className="oac-founder-ops-list__title">Login protection</span>
                    <span className="oac-founder-ops-list__meta">
                      {loginSecurity.loginProtectionLabel}
                    </span>
                  </div>
                  <span
                    className={`oac-founder-badge oac-founder-badge--${
                      loginSecurity.loginProtectionStatus === "protected" ? "healthy" : "problem"
                    }`}
                  >
                    {loginSecurity.loginProtectionLabel}
                  </span>
                </li>
                <li className="oac-founder-ops-list__item">
                  <div>
                    <span className="oac-founder-ops-list__title">Last successful login</span>
                    <span className="oac-founder-ops-list__meta">
                      {loginSecurity.lastSuccessfulLogin
                        ? new Date(loginSecurity.lastSuccessfulLogin).toLocaleString()
                        : "—"}
                    </span>
                  </div>
                </li>
                <li className="oac-founder-ops-list__item">
                  <div>
                    <span className="oac-founder-ops-list__title">Recent failed attempts</span>
                    <span className="oac-founder-ops-list__meta">
                      {loginSecurity.recentFailedAttempts} in the last 24 hours
                    </span>
                  </div>
                </li>
                <li className="oac-founder-ops-list__item">
                  <div>
                    <span className="oac-founder-ops-list__title">Active Founder sessions</span>
                    <span className="oac-founder-ops-list__meta">{loginSecurity.activeSessions}</span>
                  </div>
                </li>
              </ul>
            ) : (
              <FounderEmptyState message="Login protection details are unavailable right now." />
            )}
          </section>
          <section className="oac-founder-ops-panel">
            <h2 className="oac-founder-ops-panel__title">Unresolved security alerts</h2>
            {alerts.length === 0 ? (
              <FounderEmptyState message="No unresolved security alerts." />
            ) : (
              <ul className="oac-founder-ops-list">
                {alerts.map((alert) => (
                  <li className="oac-founder-ops-list__item" key={alert.id}>
                    <div>
                      <span className="oac-founder-ops-list__title">{formatFounderSecurityActivity(alert)}</span>
                      <span className="oac-founder-ops-list__meta">{alert.message ?? alert.severity ?? "alert"}</span>
                    </div>
                    <FounderIconButton
                      kind="edit"
                      label="Resolve alert"
                      onClick={() => {
                        setResolveAlertId(alert.id);
                        setResolveNote("");
                      }}
                    />
                  </li>
                ))}
              </ul>
            )}
            {resolveAlertId ? (
              <FounderConfirmPublish
                message="Resolve this security alert?"
                onCancel={() => setResolveAlertId(null)}
                onConfirm={async () => {
                  await resolveFounderSecurityAlert(resolveAlertId, resolveNote.trim());
                  setAlerts((rows) => rows.filter((row) => row.id !== resolveAlertId));
                  setResolveAlertId(null);
                  setMessage("Security alert resolved.");
                }}
              >
                <textarea
                  onChange={(e) => setResolveNote(e.target.value)}
                  placeholder="Resolution note"
                  rows={3}
                  value={resolveNote}
                />
              </FounderConfirmPublish>
            ) : null}
          </section>
        </>
      ) : null}

      {!loading && tab === "publishing" ? (
        <section className="oac-founder-ops-panel">
          <div className="oac-founder-ops-panel__head">
            <h2 className="oac-founder-ops-panel__title">Publishing rules</h2>
            {!editingPublishing ? (
              <FounderIconButton
                kind="edit"
                label="Edit publishing rules"
                onClick={() => {
                  setDraftMode(publicationMode === "instant" ? "instant" : "review_required");
                  setEditingPublishing(true);
                  setConfirmPublish(false);
                  setMessage("");
                }}
              />
            ) : null}
          </div>
          {!editingPublishing ? (
            <p className="oac-founder-ops-panel__lead">{formatPublicationModeLabel(publicationMode)}</p>
          ) : (
            <div className="oac-founder-ops-inline-form oac-founder-ops-inline-form--stack">
              <label className="oac-founder-ops-radio">
                <input
                  checked={draftMode === "review_required"}
                  name="publication-mode"
                  onChange={() => setDraftMode("review_required")}
                  type="radio"
                />
                Require review before publishing
              </label>
              <label className="oac-founder-ops-radio">
                <input
                  checked={draftMode === "instant"}
                  name="publication-mode"
                  onChange={() => setDraftMode("instant")}
                  type="radio"
                />
                Instant publishing
              </label>
              <div className="oac-founder-ops-detail__actions">
                {draftMode !== publicationMode ? (
                  <FounderIconButton
                    kind="publish"
                    label="Publish update"
                    onClick={() => setConfirmPublish(true)}
                  />
                ) : null}
                <FounderIconButton
                  kind="cancel"
                  label="Cancel edit"
                  onClick={() => {
                    setEditingPublishing(false);
                    setConfirmPublish(false);
                  }}
                />
              </div>
            </div>
          )}
          {confirmPublish ? (
            <FounderConfirmPublish
              message={`Confirm publishing mode change to ${formatPublicationModeLabel(draftMode)}?`}
              onCancel={() => setConfirmPublish(false)}
              onConfirm={publishPublishingRules}
              requirePassword
              submitting={submitting}
            />
          ) : null}
          {message ? <p className="oac-founder-ops-detail__result">{message}</p> : null}
        </section>
      ) : null}

      {!loading && tab === "legal" ? (
        <section className="oac-founder-ops-panel">
          <div className="oac-founder-ops-panel__head">
            <h2 className="oac-founder-ops-panel__title">Legal documents</h2>
            <FounderIconButton kind="edit" label="Publish new version" onClick={() => setCreatingLegal(true)} />
          </div>
          {legalDocs.length === 0 ? (
            <FounderEmptyState message="No legal documents found." />
          ) : (
            <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
              {legalDocs.map((doc) => (
                <li className="oac-founder-ops-list__item" key={doc.id}>
                  <div>
                    <span className="oac-founder-ops-list__title">{doc.title}</span>
                    <span className="oac-founder-ops-list__meta">
                      {doc.document_type} · v{doc.version} · {doc.effective_date}
                      {doc.is_current ? " · current" : ""}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {creatingLegal ? (
            <div className="oac-founder-ops-edit-panel">
              <div className="oac-founder-field">
                <label htmlFor="legal-type">Document type</label>
                <select
                  id="legal-type"
                  onChange={(e) =>
                    setLegalDraft({
                      ...legalDraft,
                      documentType: e.target.value as typeof legalDraft.documentType,
                    })
                  }
                  value={legalDraft.documentType}
                >
                  <option value="publisher_agreement">Publisher Agreement</option>
                  <option value="privacy_policy">Privacy Policy</option>
                  <option value="disclaimer">Disclaimer</option>
                </select>
              </div>
              <div className="oac-founder-field">
                <label htmlFor="legal-version">Version</label>
                <input
                  id="legal-version"
                  onChange={(e) => setLegalDraft({ ...legalDraft, version: e.target.value })}
                  value={legalDraft.version}
                />
              </div>
              <div className="oac-founder-field">
                <label htmlFor="legal-title">Title</label>
                <input
                  id="legal-title"
                  onChange={(e) => setLegalDraft({ ...legalDraft, title: e.target.value })}
                  value={legalDraft.title}
                />
              </div>
              <div className="oac-founder-field">
                <label htmlFor="legal-effective">Effective date</label>
                <input
                  id="legal-effective"
                  onChange={(e) => setLegalDraft({ ...legalDraft, effectiveDate: e.target.value })}
                  type="date"
                  value={legalDraft.effectiveDate}
                />
              </div>
              <div className="oac-founder-field">
                <label htmlFor="legal-content">Content</label>
                <textarea
                  id="legal-content"
                  onChange={(e) => setLegalDraft({ ...legalDraft, content: e.target.value })}
                  rows={6}
                  value={legalDraft.content}
                />
              </div>
              <FounderConfirmPublish
                message="Publish this legal document version?"
                onCancel={() => setCreatingLegal(false)}
                onConfirm={async (confirmPassword) => {
                  if (!confirmPassword) {
                    setMessage("Enter your current password to confirm this change.");
                    return;
                  }
                  try {
                    await createFounderLegalDocument({ ...legalDraft, confirmPassword });
                    const docs = await fetchFounderLegalDocuments();
                    setLegalDocs(docs);
                    setCreatingLegal(false);
                    setMessage("Legal document published.");
                  } catch {
                    setMessage("Could not publish legal document.");
                  }
                }}
                requirePassword
              />
            </div>
          ) : null}
        </section>
      ) : null}

      {!loading && tab === "audit" ? (
        <section className="oac-founder-ops-panel">
          <h2 className="oac-founder-ops-panel__title">Audit log</h2>
          {logs.length === 0 ? (
            <FounderEmptyState message="No audit entries yet." />
          ) : (
            <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
              {logs.map((log) => (
                <li className="oac-founder-ops-list__item" key={log.id}>
                  <span className="oac-founder-ops-list__title">{formatFounderAuditActivity(log)}</span>
                  <time className="oac-founder-ops-list__meta" dateTime={log.created_at}>
                    {log.created_at}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </FounderOperationalShell>
  );
}
